import { UnstyledLinkProps, UnstyledLink } from '@ardoq/link';
import {
  colors,
  easingTokens,
  radius,
  shadows,
  space,
  transitionTokens,
} from '@ardoq/design-tokens';
import { TextInput, TextInputStyle } from '@ardoq/forms';
import { Icon, MinimalIconProps } from '@ardoq/icons';
import { Box, FlexBox, Stack } from '@ardoq/layout';
import { Text, typographyMixins } from '@ardoq/typography';
import {
  AriaAttributes,
  ReactNode,
  RefObject,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import {
  cancelSubmenuAim,
  runUnlessAimingAtSubmenu,
  startSubmenuAim,
  toPoint,
} from './submenuAim';

export enum DropdownMaxWidth {
  STANDARD = '320px',
  NONE = 'none',
}

export enum DropdownMaxHeight {
  STANDARD = '300px',
  LARGE = '400px',
  /**
   * Should only be used in combination with the Popover2025 component
   */
  AUTO = '100dvh',
}

export const StyledDropdownContainer = styled.div<{
  $maxWidth: DropdownMaxWidth;
}>`
  background-color: ${colors.surfaceDefault};
  box-shadow: ${shadows.shadowM};
  border-radius: ${radius.r4};

  max-width: ${props => props.$maxWidth};
`;

const StyledScrollableContent = styled.div<{ $maxHeight: string }>`
  ${props => `max-height: ${props.$maxHeight};`}
  overflow: auto;
`;

type DropdownTriggerProps = {
  isOpen: boolean;
  openDropdown: VoidFunction;
};

/**
 * Custom hook that handles arrow key navigation from a dropdown trigger to focus into the dropdown
 * Expects the trigger element to have `aria-controls` pointing to the dropdown/popover ID
 * and a dropdown element with `data-dropdown-container` attribute
 * Intended to be used with the Popover 2025
 */
export const useDropdownTriggerContainer = <T extends HTMLElement>(
  props: DropdownTriggerProps
) => {
  const triggerContainerRef = useRef<T>(null);

  useEffect(() => {
    const container = triggerContainerRef.current;
    if (!container) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();

        if (!props.isOpen) {
          return props.openDropdown();
        }

        // Get the popover ID from aria-controls
        const popoverId = container.getAttribute('aria-controls');
        if (!popoverId) return;

        // Find the popover by ID and then its dropdown container
        const popover = document.getElementById(popoverId);
        const dropdown = popover?.querySelector('[data-dropdown-container]');
        if (!dropdown) return;

        const firstFocusable = dropdown.querySelector(
          '[role="option"]'
        ) as HTMLElement;

        if (firstFocusable) {
          firstFocusable.focus();
        }
      }
    };

    container.addEventListener('keydown', handleKeyDown);
    return () => container.removeEventListener('keydown', handleKeyDown);
  }, [props]);

  return triggerContainerRef;
};

type DelayedAction = {
  schedule: (action: VoidFunction) => void;
  cancel: VoidFunction;
};

const useDelayedAction = (delayMs: number): DelayedAction => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancel = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const schedule = (action: VoidFunction) => {
    cancel();
    timeoutRef.current = setTimeout(action, delayMs);
  };

  useEffect(() => cancel, []);

  return { schedule, cancel };
};

type StickyAfterChildHover = {
  markChildHovered: VoidFunction;
  hasChildBeenHovered: () => boolean;
};

/** Once the submenu has been hovered, it stays open until it's closed. */
const useStickyAfterChildHover = (isOpen: boolean): StickyAfterChildHover => {
  const hasBeenHoveredRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      hasBeenHoveredRef.current = false;
    }
  }, [isOpen]);

  return {
    markChildHovered: () => {
      hasBeenHoveredRef.current = true;
    },
    hasChildBeenHovered: () => hasBeenHoveredRef.current,
  };
};

type SubmenuAim = {
  deferWhileAiming: (event: React.MouseEvent, action: VoidFunction) => void;
  handleTriggerLeave: (event: React.MouseEvent) => void;
  handleChildEnter: VoidFunction;
};

/**
 * Holds back trigger hovers while the pointer is on its way from an open
 * submenu trigger to its submenu, so crossing sibling triggers doesn't close it.
 */
const isMovingIntoChild = (
  event: React.MouseEvent,
  childPopover: HTMLElement | null
): boolean =>
  event.relatedTarget instanceof Node &&
  Boolean(childPopover?.contains(event.relatedTarget));

const useSubmenuAim = (
  isOpen: boolean,
  childPopoverRef: RefObject<HTMLElement | null>
): SubmenuAim => {
  const cancelPendingEnterRef = useRef<VoidFunction | null>(null);

  useEffect(() => () => cancelPendingEnterRef.current?.(), []);

  const deferWhileAiming = (event: React.MouseEvent, action: VoidFunction) => {
    cancelPendingEnterRef.current = runUnlessAimingAtSubmenu(
      toPoint(event),
      action
    );
  };

  const handleTriggerLeave = (event: React.MouseEvent) => {
    cancelPendingEnterRef.current?.();
    cancelPendingEnterRef.current = null;

    const childPopover = childPopoverRef.current;
    if (isOpen && childPopover && !isMovingIntoChild(event, childPopover)) {
      startSubmenuAim(toPoint(event), childPopover.getBoundingClientRect());
    }
  };

  return {
    deferWhileAiming,
    handleTriggerLeave,
    handleChildEnter: cancelSubmenuAim,
  };
};

const OPEN_DELAY_MS = 150;
const CLOSE_DELAY_MS = 150;

const isMovingIntoDropdown = (
  event: React.MouseEvent,
  childPopover: HTMLElement | null
): boolean => {
  if (isMovingIntoChild(event, childPopover)) {
    return true;
  }
  const relatedTarget = event.relatedTarget;
  return (
    relatedTarget instanceof Element &&
    Boolean(relatedTarget.closest('[data-dropdown-container]'))
  );
};

export const useDebouncedNestedPopover = (
  isOpen: boolean,
  setIsOpen: (isOpen: boolean) => void
) => {
  const childPopoverRef = useRef<HTMLDivElement>(null);
  const openDelay = useDelayedAction(OPEN_DELAY_MS);
  const closeDelay = useDelayedAction(CLOSE_DELAY_MS);
  const { markChildHovered, hasChildBeenHovered } =
    useStickyAfterChildHover(isOpen);
  const submenuAim = useSubmenuAim(isOpen, childPopoverRef);

  const handleTriggerMouseEnter = (
    event: React.MouseEvent,
    onEnter?: VoidFunction
  ) => {
    closeDelay.cancel();
    submenuAim.deferWhileAiming(event, () => {
      onEnter?.();
      if (!isOpen) {
        openDelay.schedule(() => setIsOpen(true));
      }
    });
  };

  const handleTriggerMouseLeave = (event: React.MouseEvent) => {
    submenuAim.handleTriggerLeave(event);
    openDelay.cancel();
    if (
      !isOpen ||
      hasChildBeenHovered() ||
      isMovingIntoDropdown(event, childPopoverRef.current)
    ) {
      return;
    }
    closeDelay.schedule(() => setIsOpen(false));
  };

  const handleChildMouseEnter = () => {
    markChildHovered();
    submenuAim.handleChildEnter();
    closeDelay.cancel();
  };

  return {
    handleTriggerMouseEnter,
    handleTriggerMouseLeave,
    handleChildMouseEnter,
    childPopoverRef,
  };
};

const handleTabKey = (
  event: KeyboardEvent,
  currentIndex: number,
  lastFocusedIndexRef: React.MutableRefObject<number>
) => {
  if (currentIndex === -1) {
    lastFocusedIndexRef.current = -1;
    return;
  }

  const newIndex = event.shiftKey ? currentIndex - 1 : currentIndex + 1;

  // Tabbing within the dropdown - update the last focused index
  lastFocusedIndexRef.current = newIndex;

  return; // Let tabbing happen naturally
};

const handleArrowKey = (
  event: KeyboardEvent,
  currentIndex: number,
  lastFocusedIndexRef: React.MutableRefObject<number>,
  focusableElements: Element[]
) => {
  event.preventDefault();

  let targetIndex: number;

  switch (event.key) {
    case 'ArrowUp': {
      targetIndex =
        currentIndex <= 0 ? focusableElements.length - 1 : currentIndex - 1;
      break;
    }

    case 'ArrowDown': {
      targetIndex =
        currentIndex >= focusableElements.length - 1 ? 0 : currentIndex + 1;
      break;
    }

    default:
      return;
  }

  (focusableElements[targetIndex] as HTMLElement).focus();
  lastFocusedIndexRef.current = targetIndex;
};

/**
 * Dropdown container with keyboard navigation. Needs the trigger element's container to use `useDropdownTriggerContainer` hook.
 * Intended to be used with the Popover 2025
 */
export const DropdownContainer = ({
  children,
  className,
  dropdownMaxWidth,
  dropdownMaxHeight,
}: {
  children: React.ReactNode;
  className?: string;
  dropdownMaxWidth?: DropdownMaxWidth;
  dropdownMaxHeight?: DropdownMaxHeight;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const lastFocusedIndexRef = useRef<number>(-1);
  const containerId = useId();

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key !== 'ArrowUp' &&
        event.key !== 'ArrowDown' &&
        event.key !== 'Tab'
      ) {
        return;
      }

      const currentFocus = document.activeElement as HTMLElement;

      // Find which dropdown container the focused element belongs to
      // Only handle keyboard navigation if the focus is within this container
      if (
        currentFocus
          .closest('[data-dropdown-container]')
          ?.getAttribute('data-dropdown-container') !== containerId
      ) {
        return;
      }

      const focusableElements = getFocusableElements(container, containerId);

      if (focusableElements.length === 0) return;

      const currentIndex = focusableElements.indexOf(currentFocus);

      return event.key === 'Tab'
        ? handleTabKey(event, currentIndex, lastFocusedIndexRef)
        : handleArrowKey(
            event,
            currentIndex,
            lastFocusedIndexRef,
            focusableElements
          );
    };

    // If the option is clicked with a mouse, and the dropdown doesn't close,
    // we need to update the last focused index to the clicked option
    // so that focus can be restored correctly if the dropdown re-renders
    // without closing.
    // We use mousedown here to capture the event before focus is moved.
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const targetOption = target.closest('[role="option"]') as HTMLElement;

      if (targetOption) {
        const focusableElements = getFocusableElements(container, containerId);
        lastFocusedIndexRef.current = focusableElements.indexOf(targetOption);
      }
    };

    container.addEventListener('keydown', handleKeyDown);
    container.addEventListener('mousedown', handleMouseDown);
    return () => {
      container.removeEventListener('keydown', handleKeyDown);
      container.removeEventListener('mousedown', handleMouseDown);
    };
  }, [containerId]);

  // Restore focus after re-render
  useEffect(() => {
    const container = containerRef.current;
    if (!container || lastFocusedIndexRef.current === -1) return;

    const focusableElements = getFocusableElements(container, containerId);

    if (focusableElements.length === 0) return;

    // Check if focus is still anywhere within this dropdown container, OR inside
    // a popover owned by one of our options (which may be rendered in a portal
    // and therefore not a DOM descendant of `container`).
    const currentFocused = document.activeElement as HTMLElement;
    const isFocusedWithinContainer = container.contains(currentFocused);
    const isFocusedInOwnedPopover = focusableElements.some(option => {
      const popoverId = option.getAttribute('aria-controls');
      if (!popoverId) return false;
      const popover = document.getElementById(popoverId);
      return Boolean(popover?.contains(currentFocused));
    });

    // Only restore focus if it was involuntarily lost (not within the container
    // or a nested popover anymore)
    if (
      !isFocusedWithinContainer &&
      !isFocusedInOwnedPopover &&
      lastFocusedIndexRef.current < focusableElements.length
    ) {
      (focusableElements[lastFocusedIndexRef.current] as HTMLElement).focus();
    }
  }, [children, containerId]);

  const isAutoHeight = dropdownMaxHeight === DropdownMaxHeight.AUTO;
  const [autoMaxHeight, setAutoMaxHeight] = useState<string>(
    DropdownMaxHeight.AUTO
  );

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!isAutoHeight || !container) return;

    const popover = container.closest('[popover]');
    if (!popover) {
      return;
    }

    const anchor = document.querySelector<HTMLElement>(
      `[aria-controls="${popover.id}"]`
    );
    if (!anchor) {
      return;
    }

    const updateMaxHeight = () => {
      const anchorRect = anchor.getBoundingClientRect();
      const popoverRect = popover.getBoundingClientRect();

      // Skip if popover hasn't been positioned by the browser yet
      if (popoverRect.width === 0 && popoverRect.height === 0) {
        return;
      }

      const anchorCenterY = anchorRect.top + anchorRect.height / 2;
      const popoverCenterY = popoverRect.top + popoverRect.height / 2;

      const popoverIsAboveAnchor = popoverCenterY < anchorCenterY;

      const scrollableContent = container.firstElementChild;
      if (!scrollableContent) return;

      const scrollableRect = scrollableContent.getBoundingClientRect();

      const availableSpace = popoverIsAboveAnchor
        ? scrollableRect.bottom
        : window.innerHeight - scrollableRect.top;

      setAutoMaxHeight(`${Math.min(window.innerHeight, availableSpace)}px`);
    };

    const resizeObserver = new ResizeObserver(updateMaxHeight);
    // Catches when popover is positioned and when viewport resizes
    resizeObserver.observe(popover);
    resizeObserver.observe(document.documentElement);

    window.addEventListener('scroll', updateMaxHeight, { capture: true });

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('scroll', updateMaxHeight, { capture: true });
    };
  }, [isAutoHeight]);

  const resolvedMaxHeight = isAutoHeight
    ? autoMaxHeight
    : dropdownMaxHeight || DropdownMaxHeight.STANDARD;

  return (
    <StyledDropdownContainer
      ref={containerRef}
      className={className}
      data-dropdown-container={containerId}
      $maxWidth={dropdownMaxWidth || DropdownMaxWidth.STANDARD}
    >
      <StyledScrollableContent $maxHeight={resolvedMaxHeight}>
        {children}
      </StyledScrollableContent>
    </StyledDropdownContainer>
  );
};

const getFocusableElements = (container: HTMLDivElement, containerId: string) =>
  Array.from(container.querySelectorAll('[role="option"]')).filter(
    option =>
      option
        .closest('[data-dropdown-container]')
        ?.getAttribute('data-dropdown-container') === containerId
  );

/**
 * Intended to be used with the Popover 2026
 */
export const DropdownMenu: React.FC<React.PropsWithChildren> = styled.menu`
  padding: calc(${space.small} - ${space.xxsmall}) 0;
  margin-block: 0;
  display: flex;
  flex-direction: column;
`;

/**
 * Intended to be used with the Popover 2026
 * Use as the input field for filtering options in a dropdown menu wrapped in DropdownMenuWithSearch
 */
export const DropdownFilterInput = styled(TextInput)`
  /** Allows the search input to stay in place when scrolling by negating the parent padding  */
  margin-top: calc(0px - (${space.small} - ${space.xxsmall}));
  position: sticky;
  top: 0;
  background-color: ${colors.surfaceDefault};
  z-index: 1;
  border-bottom: 1px solid ${colors.borderSubtle};
  width: 100%;
  border-top-right-radius: ${radius.r4};
  border-top-left-radius: ${radius.r4};
  ${TextInputStyle}, ${TextInputStyle}:focus {
    box-shadow: none;
    height: 36px;
  }
`;

export const DropdownDivider = styled.div`
  height: 1px;
  border-bottom: 1px solid ${colors.borderSubtle};
  margin: ${space.xsmall} 0;
`;

const DropdownOptionWrapper = styled.div`
  padding: ${space.xsmall} ${space.small};
  border-radius: 6px;
  gap: 8px;
  overflow-wrap: anywhere;
  min-height: 26px;
  display: grid;
  align-items: center;
  transition: background-color ${transitionTokens.medium}
    ${easingTokens.cubicInOut};
`;

export type DropdownOptionContentProps = {
  label: string;
  rightContent?: ReactNode;
  icon?: MinimalIconProps;
  description?: string;
};

export const DropdownOptionContent = ({
  label,
  rightContent,
  icon,
  description,
}: DropdownOptionContentProps) => (
  <Stack gap="xsmall">
    <FlexBox align="center" gap="small">
      {icon && <Icon iconName={icon.name} color={icon.color ?? colors.icon} />}
      <Box flexGrow={1}>{label}</Box>
      {rightContent}
    </FlexBox>
    {description && (
      <Text variant="caption" color="textSubtle">
        {description}
      </Text>
    )}
  </Stack>
);

/**
 * Intended to be used with the Popover 2025
 */
const StyledDropdownOption = styled.button.attrs({ role: 'option' })`
  all: unset;
  padding: ${space.xxsmall} ${space.small};
  color: ${colors.textDefault};
  cursor: pointer;
  &[aria-disabled='true'] {
    cursor: not-allowed;
    color: ${colors.textDisabled};
    background-color: ${colors.surfaceDisabled};
  }

  &[aria-expanded='true'] > ${DropdownOptionWrapper} {
    background-color: ${colors.surfaceSelected};
  }

  &:not([aria-disabled='true']):hover
  > ${DropdownOptionWrapper},
  &:focus-visible
  > ${DropdownOptionWrapper} {
    background-color: ${colors.surfaceHover};
  }
`;

export type DropdownOptionProps = Pick<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  | 'onClick'
  | 'onKeyDown'
  | 'children'
  | 'onMouseEnter'
  | 'onMouseLeave'
  | 'onFocus'
  | 'onBlur'
  | 'draggable'
  | 'onDragStart'
  | 'onDragOver'
  | 'onDragEnd'
> & {
  ref?: React.Ref<HTMLButtonElement>;
  role?: React.AriaRole;
  isDisabled?: boolean;
  'data-tooltip-text'?: string;
} & AriaAttributes;

export const DropdownOption = ({
  children,
  isDisabled,
  onClick,
  ...props
}: DropdownOptionProps) => {
  return (
    <StyledDropdownOption
      {...props}
      aria-disabled={isDisabled}
      onClick={isDisabled ? undefined : onClick}
    >
      <DropdownOptionWrapper>{children}</DropdownOptionWrapper>
    </StyledDropdownOption>
  );
};

const StyledDropdownLabel = styled.div<{ $isHeader: boolean }>`
  padding: ${space.xxsmall} ${space.small};
  color: ${props => (props.$isHeader ? colors.textSubtle : colors.textDefault)};
  ${props => props.$isHeader && typographyMixins.header5}
`;

export type DropdownLabelVariant = 'default' | 'header';

export const DropdownLabel = ({
  children,
  variant = 'default',
  ['data-tooltip-text']: tooltipText,
}: {
  children: React.ReactNode;
  variant?: DropdownLabelVariant;
  'data-tooltip-text'?: string;
}) => {
  return (
    <StyledDropdownLabel
      $isHeader={variant === 'header'}
      data-tooltip-text={tooltipText}
    >
      <DropdownOptionWrapper>{children}</DropdownOptionWrapper>
    </StyledDropdownLabel>
  );
};

const StyledDropdownLink = styled(UnstyledLink).attrs({ role: 'option' })`
  all: unset;
  padding: ${space.xxsmall} ${space.small};
  color: ${colors.textDefault};
  cursor: pointer;
  display: block;

  &:hover
  > ${DropdownOptionWrapper},
  &:focus-visible
  > ${DropdownOptionWrapper} {
    background-color: ${colors.surfaceHover};
  }
`;

export type DropdownLinkProps = UnstyledLinkProps & {
  children?: React.ReactNode;
  'data-tooltip-text'?: string;
};

export const DropdownLink = ({ children, ...props }: DropdownLinkProps) => {
  return (
    <StyledDropdownLink {...props}>
      <DropdownOptionWrapper>{children}</DropdownOptionWrapper>
    </StyledDropdownLink>
  );
};
